/**
 * URL パラメータ（address / chiban / kaokuNumber）から自動検索の対象を 1 つに絞り込む純粋関数。
 *
 * R.E.DATA on Salesforce 連携（実装計画書 4.3 節）で、SSO 引き継ぎ後の地図初期表示に使う。
 * 値の優先順位はビジネスルールとして固定されており、`useMapUrlParams` の分岐から切り出すことで
 * 単体テストで組み合わせを固定できるようにする。
 */
export type AutoSearchTarget =
  | { kind: "address"; address: string }
  | { kind: "chiban"; searchTerm: string }
  | { kind: "none" };

export interface ResolveAutoSearchTargetParams {
  address: string | null;
  chiban: string | null;
  kaokuNumber: string | null;
}

/**
 * 優先順位: `address` > 地番検索（`chiban` 優先、無ければ `kaokuNumber`）> 何もしない。
 *
 * - `address` があれば `address` のみで自動検索する（`chiban` / `kaokuNumber` は使わない）。
 * - `address` が無く `chiban` または `kaokuNumber` があれば地番検索する。検索語は `chiban` を
 *   優先し、無ければ `kaokuNumber` を用いる。家屋番号からの地番変換は将来対応のため、当面は
 *   `kaokuNumber` も地番と同じ検索語として扱い、座標が得られなければ検索欄に値が入った状態で
 *   利用者に地図上で訂正してもらう。
 * - 上記のいずれにも該当しなければ自動検索しない。
 */
export const resolveAutoSearchTarget = ({
  address,
  chiban,
  kaokuNumber,
}: ResolveAutoSearchTargetParams): AutoSearchTarget => {
  if (address) {
    return { kind: "address", address };
  }
  const searchTerm = chiban ?? kaokuNumber;
  if (searchTerm) {
    return { kind: "chiban", searchTerm };
  }
  return { kind: "none" };
};
